// Admin API for workflow triggers and runs.
//
// Triggers are the ways a workflow is instantiated: a schedule, an API endpoint, an inbound
// webhook, or a call from another workflow. The trigger *catalog* (`/trigger-types`) drives the
// forms, so field lists are never hardcoded here.
import * as config from "../config.js";

const adminUrl = () => `${config.REST_SERVER_URL}/admin/workflow`;

const encode = (value) => encodeURIComponent(value ?? "");

/**
 * One request, with the HTTP status preserved so callers can distinguish 400 / 403 / 404.
 * @returns {Promise<{ok: boolean, status: number, data: object}>}
 */
const apiRequest = async (method, path = "", body) => {
  let response;

  try {
    response = await fetch(`${adminUrl()}${path}`, {
      method,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    console.log("WORKFLOW TRIGGER REQUEST ERROR: ", error);
    return {
      ok: false,
      status: 0,
      data: { success: false, message: error?.message || "Request failed." },
    };
  }

  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    return {
      ok: false,
      status: response.status,
      data: { success: false, message: `Could not read the response: ${error?.message}` },
    };
  }

  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    data = {
      success: false,
      message: `Unexpected non-JSON response (HTTP ${response.status}): ${String(text).slice(0, 200)}`,
    };
  }

  return { ok: response.ok, status: response.status, data };
};

const flatten = ({ ok, status, data }) => ({
  ...data,
  success: ok && data?.success !== false,
  status,
});

/** The trigger catalog: which types exist and which fields each one renders. */
export const getTriggerTypes = async () => {
  const result = flatten(await apiRequest("GET", "/trigger-types"));
  return {
    ...result,
    trigger_types: Array.isArray(result?.trigger_types) ? result.trigger_types : [],
    types: result?.types ?? {},
  };
};

/** Normalise a draft config and preview the next runs (schedule) server-side. */
export const previewTrigger = async ({ type, config: triggerConfig, count = 5 }) =>
  flatten(await apiRequest("POST", "/triggers/preview", { type, config: triggerConfig, count }));

export const getTriggers = async (workflowPublicId) => {
  const result = flatten(await apiRequest("GET", `/workflow-graph/${encode(workflowPublicId)}/triggers`));
  return { ...result, triggers: Array.isArray(result?.triggers) ? result.triggers : [] };
};

export const createTrigger = async (workflowPublicId, payload) =>
  flatten(await apiRequest("POST", `/workflow-graph/${encode(workflowPublicId)}/triggers`, payload));

export const updateTrigger = async (triggerPublicId, payload) =>
  flatten(await apiRequest("PUT", `/triggers/${encode(triggerPublicId)}`, payload));

/** Partial update — used for the enable/disable switch. */
export const patchTrigger = async (triggerPublicId, payload) =>
  flatten(await apiRequest("PATCH", `/triggers/${encode(triggerPublicId)}`, payload));

export const deleteTrigger = async (triggerPublicId) =>
  flatten(await apiRequest("DELETE", `/triggers/${encode(triggerPublicId)}`));

/** Run this workflow via the trigger, now. */
export const runTrigger = async (triggerPublicId, { input, context } = {}) =>
  flatten(
    await apiRequest("POST", `/triggers/${encode(triggerPublicId)}/run`, {
      ...(input === undefined ? {} : { input }),
      ...(context === undefined ? {} : { context }),
    })
  );

export const getRuns = async (
  workflowPublicId,
  { limit = 50, offset = 0, status = "", triggerId = "" } = {}
) => {
  const query = new URLSearchParams();
  query.set("limit", String(limit));
  query.set("offset", String(offset));
  if (status) query.set("status", status);
  if (triggerId) query.set("trigger_id", String(triggerId));

  const result = flatten(
    await apiRequest("GET", `/workflow-graph/${encode(workflowPublicId)}/runs?${query.toString()}`)
  );

  return {
    ...result,
    runs: Array.isArray(result?.runs) ? result.runs : [],
    total: Number(result?.total ?? 0),
  };
};

/** A run with its `input` / `context` / `output` payloads. */
export const getRun = async (runPublicId) => flatten(await apiRequest("GET", `/runs/${encode(runPublicId)}`));

/** The client's workflows — used to populate the sub-workflow caller picker. */
export const getClientWorkflows = async (clientPublicId) => {
  const result = flatten(await apiRequest("GET", `/client-workflows/${encode(clientPublicId)}`));
  return { ...result, workflows: Array.isArray(result?.workflows) ? result.workflows : [] };
};

export default getTriggerTypes;
